import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, switchMap, tap, filter } from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { UsersService } from '@app/services/users/users.service';
import * as UserActions from '../actions/user.actions';
import { JwtService } from '@app/core/services/jwt.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class UserEffects {

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUsers),
      switchMap(() => {
        return this._users.onGetUsers('usrUsername', 'asc').pipe(
          map(({ data })=> {
            
            // Add Admin role to users with usrIsPrivileged = 1
            /*const usersWithAdminRole = data.users.map(user => {
              if (user.usrIsPrivileged === 1 && !user.usrRoles.includes('Admin')) {
                user.usrRoles.push('Admin');
              }
              return user;
            });*/

            // return UserActions.loadUsersSuccess({ users: usersWithAdminRole, total: data.total })}),
            return UserActions.loadUsersSuccess({ users: data.users, total: data.total })}),
          catchError(error => of(UserActions.loadUsersFailure({ error }))))
      }
      )
    );
  });

  loadUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUser),
      switchMap((action) => {
        return this._users.onGetUser(action.id).pipe(
          map(user => UserActions.loadUserSuccess({ user })),
          catchError(error => of(UserActions.loadUserFailure({ error }))))
      }
      )
    );
  });

  // saveUser$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(UserActions.saveUser),
  //     switchMap((action) => {
  //       return this._jwt.onSaveUser(action).pipe(
  //         filter(({ user }) => user !== undefined),
  //         map(({ user }) => {
  //             return UserActions.saveUserSuccess({ user: user})
  //         }),
  //         catchError(error => of(UserActions.saveUserFailure({ error }))))
  //     }
  //     )
  //   );
  // });

  /*saveUser$ = createEffect(() =>
  this.actions$.pipe(
    ofType(UserActions.saveUser),
    switchMap((action) => {
      return this._jwt.onSaveUser(action).pipe(
        map(({ user }) => {
          if(user!==undefined){
            return UserActions.saveUserSuccess({ user });
          }
          else{
            return UserActions.saveUserFailure({ error: 'An error occurred.' });
          }
        }),
        catchError(error => {
          let errorMessage = 'An error occurred.';
          if (error && error.message) {
            errorMessage = error.message;
          }
          return of(UserActions.saveUserFailure({ error: errorMessage }));
        })
      );
    })
  )
);*/

saveUser$ = createEffect(() =>
  this.actions$.pipe(
    ofType(UserActions.saveUser),
    switchMap((action) => {
      return this._jwt.onSaveUser(action).pipe(
        map(response => {
          if(response.result!='undefined' && response.result=='failed.max'){
            this.store$.dispatch(CoreActions.showErrorMessage({
              title: this._translation.instant('errormessage.warning'),
              body: this._translation.instant('errormessage.limituser'),
              show_upgrade: true
            }));
            return UserActions.saveUserFailure({ error: this._translation.instant('error.occurred') });
          }else{
            const { user } = response;
            if (user !== undefined) {
              // Add Admin role to users with usrIsPrivileged = 1
              // if (user.usrIsPrivileged === 1 && !user.usrRoles.includes('Admin')) {
              //   user.usrRoles.push('Admin');
              // }
              
              this.store$.dispatch(CoreActions.increaseCurrentLimit({ types: 'users' }));
              const translatedMessage = this._translation.instant(response.message);
              this._toastr.success(translatedMessage, '', { timeOut: 5000 });
              return UserActions.saveUserSuccess({ user });
            } else {
              const translatedMessage = this._translation.instant(response.message, { DATA: response.data });
              this._toastr.error(translatedMessage, '', { timeOut: 5000 });
              return UserActions.saveUserFailure({ error: this._translation.instant('error.occurred') });
            }
          }
        }),
        catchError(error => {
          let errorMessage = this._translation.instant('error.occurred');
          if (error && error.message) {
            errorMessage = error.message;
          }
          // Backend 422/403 carry a translatable key in error.error.message.
          this._toastr.error(this.backendMessage(error, errorMessage), '', { timeOut: 5000 });
          return of(UserActions.saveUserFailure({ error: errorMessage }));
        })
      );
    })
  )
);



  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUser),
      switchMap((action) => {
        return this._users.onUpdateUser(action.id, action.user).pipe(
          map(({ user, message, error, data }) => {
            const translatedMessage = this._translation.instant(message, { DATA: data });
            if(error){
              this._toastr.error(translatedMessage, '', { timeOut: 3000 });
              return UserActions.updateUserFailture({ error: message });
            }else{
              // Add Admin role to users with usrIsPrivileged = 1
              if (user.usrIsPrivileged === 1 && !user.usrRoles.includes('Admin')) {
                user.usrRoles.push('Admin');
              }
          
              this._toastr.success(translatedMessage, '', { timeOut: 3000 });
              return UserActions.updateUserSuccess({ user: user })
            }
          }),
          catchError(error => {
            this._toastr.error(this.backendMessage(error, this._translation.instant('error.occurred')), '', { timeOut: 3000 });
            return of(UserActions.updateUserFailture({ error }));
          }))
      }
      )
    );
  });

  private backendMessage(error: any, fallback: string): string {
    const key = error?.error?.message;
    return key ? this._translation.instant(key, { DATA: error?.error?.data }) : fallback;
  }

  deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.deleteUser),
      switchMap((action) => {
        return this._users.onDeleteUser(action.id).pipe(
          // tap(() => this._toastr.error(`Successfully deleted user.`)),
          map((item: any) => {
            if (item?.data?.toastr?.key !== 'toastr.notDeletedUser') {
              this.store$.dispatch(CoreActions.decreaseCurrentLimit({ types: 'users' }));
              return UserActions.deleteUserSuccess({ id: action.id });
            }else{
              return UserActions.noDeleteUserSuccess({ id: action.id });
            } 
          }),
          catchError(error => of(UserActions.loadUsersFailure({ error }))))
      }
      )
    );
  });

  checkUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.checkUser),
      switchMap((action) => {
        return this._users.onCheckUser(action).pipe(
          map((res) => {
            return UserActions.deleteUserSuccess(res)
          }),
          catchError(error => of(UserActions.loadUsersFailure({ error }))))
      })
    );
  });

  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.sortUsers),
      switchMap((action) => {
        return this._users.onGetUsers(action.sort, action.direction).pipe(
          map(({ data })=> UserActions.loadUsersSuccess({ users: data.users, total: data.total })),
          catchError(error => of(UserActions.loadUsersFailure({ error }))))
      }
      )
    );
  });

  /* Start Multilocations Effects */
  changeToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.changeToken),
      switchMap((action) => {
        return this._users.onChangeToken(action.id,action.ids).pipe(
          map(() => UserActions.changeTokenSuccess()),
          catchError(error => of(UserActions.changeTokenFailure({ error }))))
      }
      )
    );
  });
  
  /* End Multilocations Effects*/
  
  constructor(private actions$: Actions, private _users: UsersService, private _userLimit: UsersService, private _jwt: JwtService, private _router: Router, private _toastr: ToastrService,private store$: Store,public _translation: TranslateService) { }

}
